import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import kikininLogo from '../assets/icons/logo.png';
import { svg } from '../assets/svg';
import { components } from '../components';
import utils from '../helpers/utils';
import { hooks, useNavigate } from '../hooks';
import { Routes, TabScreens } from '../routes';
import { RootState } from '../store';
import { removePaymentMethod, removeUserPoints } from '../store/slices/appSlice';
import { setLogout } from '../store/slices/authSlice';
import { clearCart } from '../store/slices/cartSlice';
import { setCurrentScreen } from '../store/slices/screenSlice';
import { setScreen } from '../store/slices/tabSlice';
import { showSuccessToast } from './Toast';

const tabs = [
  { id: 1, screen: TabScreens.Home, text: 'Home', icon: <svg.HomeTabSvg /> },
  { id: 2, screen: TabScreens.Menu, text: 'Menu', icon: <svg.MenuTabSvg /> },
  { id: 3, screen: TabScreens.Order, text: 'Order', icon: <svg.OrderTabSvg /> },
];

const modalMenu = [
  { id: 1, title: 'My Orders', route: Routes.OrderHistory, showArrow: true },
  { id: 2, title: 'Payment Methods', route: Routes.PaymentMethods, showArrow: true },
  { id: 3, title: 'About Us', route: '', url: 'https://www.kickininn.com.au/about-us/', showArrow: true },
  { id: 4, title: 'Logout', route: Routes.SignIn },
];

export const Footer: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = useNavigate();
  const currentTabScreen = useSelector((state: RootState) => state.tabSlice.screen);
  const { user } = useSelector((state: RootState) => state.authReducer.auth);
  const cartItems = useSelector((state: RootState) => state.cartReducer.cartItems);

  const [showModal, setShowModal] = useState(false);
  const [themeColor, setThemeColor] = useState('#F6F9F9');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor && themeColor) {
      metaThemeColor.setAttribute('content', themeColor);
    }
  }, [themeColor, showModal]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handelNavigation = (item: any) => {
    if (item.route === Routes.SignIn) {
      dispatch(setScreen(TabScreens.Home));
      dispatch(setCurrentScreen(TabScreens.Home));
      navigate(Routes.TabNavigator);
      utils.clearLocalStorage();
      dispatch(clearCart());
      dispatch(setLogout());
      dispatch(removePaymentMethod());
      dispatch(setCurrentScreen(''));
      dispatch(removeUserPoints());
      showSuccessToast('Signout successfully.');
      navigate(Routes.TabNavigator);
    } else if (item.url) {
      window.open(item.url, '_blank');
    } else {
      navigate(item.route);
    }
  };

  const renderModal = (): JSX.Element | null => {
    if (!showModal || !user) return null;
    return (
      <>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(30, 37, 56, 0.6)",
            zIndex: 101,
            cursor: "pointer",
          }}
          onClick={() => {
            setThemeColor("#F6F9F9");
            setShowModal(false);
          }}
        />

        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "80%",
            backgroundColor: "var(--white-color)",
            zIndex: 102,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              margin: 20,
              padding: 20,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 15,
              background: "var(--border-color)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.13)",
              cursor: "pointer"
            }}
            onClick={() => {
              navigate(Routes.EditProfile);
            }}
          >
            <div style={{ display: "flex", flexDirection: "row", gap: 14, alignItems: "center", flex: 1, minWidth: 0 }}>
              <div
                style={{
                  width: isMobile ? 40 : 60,
                  height: isMobile ? 40 : 60,
                  borderRadius: "50%",
                  color: "var(--white-color)",
                  background: "var(--main-turquoise)",
                  fontSize: 24,
                  flexShrink: 0,
                }}
                className="t16 center"
              >
                {user.name[0]}
              </div>

              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <span className="t16" style={{ color: "var(--main-color)", fontWeight: 500 }}>
                  {user.name}
                </span>
                <span className="t14" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 3 }}>
                  {user.email}
                </span>
                <span className="t14" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user.phone}
                </span>
              </div>
            </div>
            {!isMobile && (
              <div style={{ width: 60, height: "auto" }}>
                <img src={kikininLogo} alt="Logo" style={{ width: "100%", height: "auto" }} />
              </div>
            )}
          </div>

          <div style={{
            height: 1,
            backgroundColor: 'rgba(0,0,0,0.1)',
            marginBottom: 8
          }} ></div>
          <ul
            style={{
              flex: 1,
              marginLeft: 4,
              marginRight: 4,
              paddingRight: 20,
              paddingLeft: 20,
              borderRadius: 15,
            }}
          >
            {modalMenu.map((item, index, array) => {
              const isLast = index === array.length - 1;
              const isSignOut = item.title === "Logout";

              return (
                <li
                  key={item.id}
                  className="row-center-space-between clickable"
                  style={{
                    padding: isSignOut ? "0" : "6px 0",
                    marginBottom: isLast ? 0 : 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => (item.route || item.url) && handelNavigation(item)}
                >
                  {isSignOut ? (
                    <components.Button
                      colorScheme='secondary'
                      text={item.title}
                      containerStyle={{
                        letterSpacing: 0.1,
                        marginTop: 10,
                        paddingLeft: 10,
                        paddingRight: 10,
                        height: 40,
                        color: "var(--main-turquoise)",
                      }}
                    />
                  ) : (
                    <>
                      <span
                        className="t16"
                        style={{
                          color: "var(--main-color)",
                        }}
                      >
                        {item.title}
                      </span>
                      {item.showArrow && <svg.RightArrowSvg />}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </>
    );
  };

  const handleClick = (tab: any) => {
    dispatch(setScreen(tab.screen));
    dispatch(setCurrentScreen(tab.screen));
  };

  return (
    <section style={{ padding: 10, backgroundColor: '#f6f9f9' }}>
      <footer
        style={{
          zIndex: 100,
          borderRadius: 10,
          height: 'var(--footer-height)',
          backgroundColor: 'var(--white-color)',
        }}
      >
        <ul style={{ height: '100%' }} className="row-center-space-around">
          {tabs.map((tab) => {
            const isActive = currentTabScreen === tab.screen;
            return (
              <li
                key={tab.id}
                className="clickable center"
                style={{
                  height: '100%',
                  width: `calc(100% / ${user ? 4 : 3})`,
                  borderRadius: 10,
                  position: 'relative',
                }}
                onClick={() => handleClick(tab)}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {tab.screen === TabScreens.Order && cartItems?.length > 0 && (
                    <div
                      className="t10"
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: isMobile ? 14 : 50,
                        backgroundColor: 'var(--main-turquoise)',
                        padding: '2px 6px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      <span
                        style={{
                          color: 'var(--white-color)',
                          fontFamily: 'DM Sans',
                          fontWeight: 700,
                          fontSize: 10,
                        }}
                      >
                        {cartItems.length}
                      </span>
                    </div>
                  )}

                  {tab.icon}
                  <span
                    className="t14"
                    style={{
                      color: isActive ? 'var(--main-turquoise)' : 'var(--main-color)',
                      marginTop: 4,
                    }}
                  >
                    {tab.text}
                  </span>
                </div>
              </li>
            );
          })}

          {user ? (
            <li
              key="user"
              className="clickable center"
              style={{
                height: '100%',
                width: `calc(100% / 4)`,
                borderRadius: 10,
              }}
              onClick={() => {
                setShowModal(true);
                setThemeColor('#fff');
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: 25,
                    height: 25,
                    borderRadius: '50%',
                    backgroundColor: 'var(--main-turquoise)',
                    color: '#fff',
                    fontSize: 16,
                  }}
                  className="center t14"
                >
                  {user.name[0]}
                </div>
                <span className="t14" style={{ color: 'var(--main-color)', marginTop: 4 }}>
                  {utils.getShortName(user.name)}
                </span>
              </div>
            </li>
          ) : (
            <li
              key="login"
              className="clickable center"
              style={{
                height: '100%',
                width: `calc(100% / 3)`,
                borderRadius: 10,
              }}
              onClick={() => navigate(Routes.SignIn)}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <svg.LoginSvg />
                <span className="t14" style={{ marginTop: 4 }}>
                  Login
                </span>
              </div>
            </li>
          )}
        </ul>
      </footer>
      {renderModal()}
    </section>
  );
};